import React from "react"
import { TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody, FontIcon } from "material-ui"
import strings from "../../localization/strings"
import Table from "../../components/common/Table"
import ColumnSelector from "../../components/common/columnSelector/ColumnSelector"
import { IJobDocument, ITableColumnSelect } from "../../models"
import { green500, red500 } from "material-ui/styles/colors"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AccountStore from "../../stores/AccountStore"
import ExternalLink from "../../components/common/externalLink/ExternalLink"
import FavoriteSelector from "../../components/common/favoriteSelector/FavoriteSelector"
import { IJobReportColumns, EFavoriteType } from "../../models"
import { checkJobFieldAvailable } from "../../helpers/jobHelper"
import { checkPhaseFieldAvailable } from "../../helpers/punchHelper"
interface IProps {
	readonly: boolean
	jobs: IJobDocument[]
	selectedJobs: IJobDocument[]
	total: number
	offset: number
	limit: number
	limitOptions: number[]
	jobColumns: IJobReportColumns
	tableColumnSelect: ITableColumnSelect[]
	onPageClick(offset: number): void
	onLimitChange(limit: number): void
	handleClickRow(jobId: string): void
	handleSelectJobs(jobs: IJobDocument[]): void
	handleChangeTableColumns(columns: ITableColumnSelect[]): void
}

const JobsTable: React.SFC<IProps> = (props: IProps) => {
	return (
		<Table
			readonly={props.readonly}
			allowClickRow
			unClickableCols={[0, 1]}
			items={props.jobs}
			handleSelect={props.handleSelectJobs}
			handleClickRow={job => props.handleClickRow(job.Id)}
			total={props.total}
			offset={props.offset}
			limit={props.limit}
			limitOptions={props.limitOptions}
			onPageClick={props.onPageClick}
			onLimitChange={props.onLimitChange}
		>
			<TableHeader>
				<TableRow>
					<TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 10 }} />
					<TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 5 }} />
					{props.jobColumns.IncludeName &&
						<TableHeaderColumn style={{ textAlign: "left", width: "auto", paddingLeft: 5, paddingRight: 5 }}>{strings.Name}</TableHeaderColumn>
					}
					{props.jobColumns.IncludeParentHierarchy &&
						<TableHeaderColumn style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Parent_Hierarchy}</TableHeaderColumn>
					}
					{props.jobColumns.IncludeJobId &&
						<TableHeaderColumn style={{ textAlign: "left", width: 150, paddingLeft: 5, paddingRight: 5 }}>{checkJobFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel} ID` : strings.Job_ID}</TableHeaderColumn>
					}
					{AccountStore.getCRMEnabled() && AccountStore.getScheduleEnabled() &&
						props.jobColumns.IncludeShifts &&
						<TableHeaderColumn style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5 }}>{strings.Shifts}</TableHeaderColumn>
					}
					{AccountStore.getPhasesEnabled() &&
						props.jobColumns.IncludePhases &&
						<TableHeaderColumn style={{ textAlign: "left", width: 100, paddingLeft: 5, paddingRight: 5 }}>{checkPhaseFieldAvailable("plural") ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.PhaseFieldLabels.PluralPhaseLabel}` :strings.Phases}</TableHeaderColumn>
					}
					{props.jobColumns.IncludeActive &&
						<TableHeaderColumn style={{ textAlign: "center", width: 80, paddingLeft: 5, paddingRight: 5 }}>{strings.Active}</TableHeaderColumn>
					}
					<TableHeaderColumn style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5, width: 50, float: Object.keys(props.jobColumns).filter(x => props.jobColumns[x] === true).length < 3 ? "right" : "none" }}>
						<ColumnSelector
							columns={props.tableColumnSelect}
							handleChange={props.handleChangeTableColumns}
						/>
					</TableHeaderColumn>
				</TableRow>
			</TableHeader>
			<TableBody deselectOnClickaway={false}>
				{props.jobs.map((job, index) => {
					const hierarchyPathLabelToRoot = hierarchyHelper.getFullPathLabel(job.HierarchyId)
					return (
						<TableRow data-intercom-target={index === 0 && "menu-Jobs-openJobDetails"} key={job.Id} selected={props.selectedJobs.find(item => item.Id === job.Id) ? true : false}>
							<TableRowColumn style={{ width: 25, paddingLeft: 5, paddingRight: 10, paddingTop: 4 }}>
								<FavoriteSelector
									itemName={job.Name}
									isFavorite={job.IsFavorite}
									objectId={job.Id}
									favoriteType={EFavoriteType.Job}
								/>
							</TableRowColumn>
							<TableRowColumn data-intercom-target={index === 0 && "menu-Jobs-openJobDetailsInNewTab"} style={{ width: 25, paddingLeft: 5, paddingRight: 5, paddingTop: 4 }}>
								<ExternalLink
									url={"/job-details/" + job.Id}
								/>
							</TableRowColumn>
							{props.jobColumns.IncludeName &&
								<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: "auto", paddingLeft: 5, paddingRight: 5 }} title={job.Name}>{job.Name}</TableRowColumn>
							}
							{props.jobColumns.IncludeParentHierarchy &&
								<TableRowColumn style={{ textAlign: "left", cursor: "pointer", paddingLeft: 5, paddingRight: 5, color: hierarchyPathLabelToRoot === strings.Hierarchy_Removed && red500 }} title={hierarchyPathLabelToRoot === strings.Hierarchy_Removed ? "" : hierarchyPathLabelToRoot}>{hierarchyPathLabelToRoot}</TableRowColumn>
							}
							{props.jobColumns.IncludeJobId &&
								<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 150, paddingLeft: 5, paddingRight: 5 }} title={job.ExternalId}>{job.ExternalId}</TableRowColumn>
							}
							{AccountStore.getCRMEnabled() && AccountStore.getScheduleEnabled() &&
								props.jobColumns.IncludeShifts &&
								<TableRowColumn style={{ textAlign: "left", cursor: "pointer", paddingLeft: 5, paddingRight: 5 }}>
									{job.NumberOfShifts > 1 ?
										"Multiple" :
										job.NumberOfShifts === 1 && job.PerDiemShift &&
										`${new Date(job.PerDiemShift.StartDateTime).toLocaleString()} to ${new Date(job.PerDiemShift.EndDateTime).toLocaleString()}`
									}
								</TableRowColumn>}
							{AccountStore.getPhasesEnabled() &&
								props.jobColumns.IncludePhases &&
								<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 100, paddingLeft: 5, paddingRight: 5 }}>
									{job.Phases.filter(i => !i.Deleted).length}
								</TableRowColumn>
							}
							{props.jobColumns.IncludeActive &&
								<TableRowColumn style={{ textAlign: "center", cursor: "pointer", width: 80, paddingLeft: 5, paddingRight: 5 }}>
									{job.Active && <FontIcon className="fa fa-check" style={{ fontSize: 18, color: green500 }} />}
								</TableRowColumn>
							}
							<TableRowColumn style={{ textAlign: "left", paddingLeft: 5, paddingRight: 5, width: 50, float: Object.keys(props.jobColumns).filter(x => props.jobColumns[x] === true).length < 3 ? "right" : "none" }}></TableRowColumn>
						</TableRow>
					)
				})}
			</TableBody>
		</Table>
	)
}

export default JobsTable